import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { CartLinesUpdateEvent } from '@shopify/events';

const SOURCE = 'cart-entries-multiplier';

/**
 * One-time 3X entries upsell for the cart.
 *
 * @extends {Component<Record<string, never>>}
 */
class CartEntriesMultiplier extends Component {
  /**
   * @returns {HTMLElement | null}
   */
  #ui() {
    if (!this.id) return null;
    return document.querySelector(`[data-multiplier-controller="${CSS.escape(this.id)}"]`);
  }

  /** @returns {string} */
  #sectionIds() {
    /** @type {string[]} */
    const ids = [];
    for (const item of document.querySelectorAll('cart-items-component')) {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        ids.push(item.dataset.sectionId);
      }
    }
    return [...new Set(ids)].join(',');
  }

  async addMultiplier() {
    if (this.hasAttribute('aria-busy')) return;

    const button = this.#ui()?.querySelector('.cart-entries-multiplier__button');
    if (button instanceof HTMLButtonElement && button.disabled) return;

    const variantId = Number(this.dataset.variantId);
    if (!Number.isFinite(variantId) || variantId <= 0) return;

    const cartHasItems = Boolean(document.querySelector('.cart-items__table-row'));
    if (!cartHasItems) return;
    this.setAttribute('aria-busy', 'true');
    if (button instanceof HTMLButtonElement) button.disabled = true;

    const deferred = CartLinesUpdateEvent.createPromise();
    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'cart',
        lines: [{ merchandiseId: String(variantId), quantity: 1 }],
        promise: deferred.promise,
      })
    );

    try {
      const body = JSON.stringify({
        items: [{ id: variantId, quantity: 1 }],
        sections: this.#sectionIds(),
        sections_url: window.location.pathname,
      });

      const response = await fetch(Theme.routes.cart_add_url, fetchConfig('json', { body }));
      const data = await response.json();

      if (data.status) {
        throw new Error(data.description || data.message || 'Add to cart failed');
      }

      const cartResponse = await fetch(`${Theme.routes.cart_url}.js`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      const cart = await cartResponse.json();

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          sections: data.sections ?? cart.sections,
          source: SOURCE,
          didError: false,
        },
      });
    } catch (error) {
      deferred.reject(error instanceof Error ? error : new Error('Add to cart failed'));
      this.removeAttribute('aria-busy');
      if (button instanceof HTMLButtonElement) button.disabled = false;
      console.warn('[cart-entries-multiplier] Add failed:', error);
    }
  }

  async cancelMultiplier() {
    if (this.hasAttribute('aria-busy')) return;

    const variantId = Number(this.dataset.variantId);
    if (!Number.isFinite(variantId) || variantId <= 0) return;

    const button = this.#ui()?.querySelector('.cart-entries-multiplier__cancel');
    this.setAttribute('aria-busy', 'true');
    if (button instanceof HTMLButtonElement) button.disabled = true;

    const deferred = CartLinesUpdateEvent.createPromise();
    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'remove',
        context: 'cart',
        lines: [{ merchandiseId: String(variantId), quantity: 0 }],
        promise: deferred.promise,
      })
    );

    try {
      const updateUrl = Theme.routes.cart_update_url.endsWith('.js')
        ? Theme.routes.cart_update_url
        : `${Theme.routes.cart_update_url}.js`;
      const body = JSON.stringify({
        updates: { [String(variantId)]: 0 },
        sections: this.#sectionIds(),
        sections_url: window.location.pathname,
      });

      const response = await fetch(updateUrl, fetchConfig('json', { body }));
      const cart = await response.json();

      if (cart.status) {
        throw new Error(cart.description || cart.message || 'Remove from cart failed');
      }

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          sections: cart.sections,
          source: SOURCE,
          didError: false,
        },
      });
    } catch (error) {
      deferred.reject(error instanceof Error ? error : new Error('Remove from cart failed'));
      this.removeAttribute('aria-busy');
      if (button instanceof HTMLButtonElement) button.disabled = false;
      console.warn('[cart-entries-multiplier] Cancel failed:', error);
    }
  }
}

if (!customElements.get('cart-entries-multiplier-component')) {
  customElements.define('cart-entries-multiplier-component', CartEntriesMultiplier);
}
